import { useEffect, useState } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { Avatar, PersonLine, Banner, Button, ConflictNote, ErrorPanel, RiskMeter, SearchInput, Spinner, StatusBadge, LinkButton as UiLinkButton, Section as UiSection, riskLevelOf, useApi, useT } from "@iga/ui";
import type { ComponentProps } from "react";

// Promoted to @iga/ui; re-exported so the pages keep their imports.
export { Avatar, PersonLine, ProgressBar, SidePanel } from "@iga/ui";
/** Portal buttons are touch-sized by default. */
export function LinkButton(p: ComponentProps<typeof UiLinkButton>) { return <UiLinkButton size="touch" {...p} />; }
/** Portal page sections use h2 titles. */
export function Section(p: ComponentProps<typeof UiSection>) { return <UiSection heading="h2" {...p} />; }
import type { Person, Risk, SodFlag, SodViolation } from "../lib/types";
import { daysUntil, errMsg, firstName, riskRank, useFmt } from "../lib/hooks";

/* ------------------------------------------------------------------ people */

/**
 * People picker backed by GET /api/portal/me/people?q= (any active identity, min 2 chars).
 * `fetchPath` switches to the service-desk search (identities:read). Results are a listbox under the field.
 */
export function PeoplePicker({ value, onChange, placeholder, exclude = [], fetchPath = "/api/portal/me/people", autoFocus, id }: { value: Person | null; onChange: (p: Person | null) => void; placeholder?: string; exclude?: string[]; fetchPath?: string; autoFocus?: boolean; id?: string }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => { const h = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(h); }, [q]);
  const enabled = debounced.length >= 2 && !value;
  const res = useApi<{ items: Person[] }>(["people", fetchPath], fetchPath, { q: debounced }, { enabled });
  if (value) {
    return (
      <div className="flex min-h-11 items-center justify-between gap-2 rounded-control border border-line-strong bg-surface px-3 py-1.5">
        <PersonLine person={value} size={28} />
        <Button variant="ghost" size="touch" className="px-2" onClick={() => { onChange(null); setQ(""); }} aria-label={t("ui.close")}><X className="h-4 w-4" aria-hidden="true" /></Button>
      </div>
    );
  }
  const items = (res.data?.items ?? []).filter((p) => !exclude.includes(p.id));
  return (
    <div className="relative">
      <SearchInput id={id} value={q} placeholder={placeholder ?? t("p.people.placeholder")} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus} aria-label={placeholder ?? t("p.people.placeholder")} autoComplete="off" />
      {enabled && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-card border border-line bg-surface shadow-e2" role="listbox">
          {res.isLoading && <div className="p-3 text-center"><Spinner /></div>}
          {res.error && <div className="p-3 text-small text-bad">{errMsg(res.error)}</div>}
          {res.data && items.length === 0 && <div className="p-3 text-small text-fg-3">{t("p.people.noMatches")}</div>}
          {items.map((p) => (
            <button key={p.id} type="button" role="option" aria-selected={false} className="flex min-h-11 w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-surface-2" onClick={() => { onChange(p); setQ(""); }}>
              <PersonLine person={p} size={28} />
            </button>
          ))}
        </div>
      )}
      {q.trim().length > 0 && q.trim().length < 2 && <div className="mt-1 text-small text-fg-3">{t("p.people.minChars")}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ access facts */

/** End date of an assignment: an "Ends in N days" badge inside 30 days, the date otherwise, "–" when open-ended. */
export function ValidTo({ value, openLabel }: { value?: string | null; openLabel?: string }) {
  const t = useT();
  const fmt = useFmt();
  if (!value) return <span className="text-fg-3">{openLabel ?? t("p.access.noEndDate")}</span>;
  const days = daysUntil(value) ?? 0;
  if (days < 0) return <StatusBadge domain="assignment" status="expired" audience="portal" compact />;
  if (days <= 30) return <StatusBadge domain="assignment" status="expiring" audience="portal" params={{ days }} compact />;
  return <span>{t("p.access.until", { date: fmt.day(value) })}</span>;
}

/** Portal risk (§2.4): the meter only from High up unless `always`; with a sentence of what it allows when known. */
export function PortalRisk({ risk, sentence, always, className }: { risk?: Risk | null; sentence?: string | null; always?: boolean; className?: string }) {
  if (!risk) return null;
  const level = riskLevelOf(risk);
  if (!always && level < 3) return null;
  return <RiskMeter level={level} sentence={level >= 3 && sentence ? sentence : undefined} className={className} />;
}

/**
 * SoD flags as ConflictNotes (§2.4). The catalogue and request items only carry the policy name, not the
 * other side, so the sentence names the policy: "Conflicts with the other side of 'AP clerk vs payment run', which Michael has."
 */
export function SodNotes({ sod, holder, className }: { sod?: (SodFlag | SodViolation)[] | null; holder: string; className?: string }) {
  const t = useT();
  if (!sod || sod.length === 0) return null;
  return (
    <ul className={clsx("flex flex-col gap-1", className)}>
      {sod.map((s) => (
        <li key={s.policyId}><ConflictNote mode={s.enforcement === "prevent" ? "prevent" : "detect"} otherSide={t("p.sod.otherSide", { policy: s.policyName })} holder={firstName(holder)} /></li>
      ))}
    </ul>
  );
}

export const highestRisk = (risks: (Risk | null | undefined)[]): Risk => risks.reduce<Risk>((m, r) => (r && riskRank[r] > riskRank[m] ? r : m), "low");

/* ------------------------------------------------------------------ layout helpers */

/** Load error on the §3 ErrorPanel: what happened / what to do. */
export function LoadError({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const t = useT();
  if (!error) return null;
  return (
    <ErrorPanel title={title ?? t("p.error.loadTitle")} what={errMsg(error)} todo={t("p.error.loadTodo")} badge={t("p.error.badge")}
      actions={onRetry && <Button variant="secondary" onClick={onRetry}>{t("p.error.retry")}</Button>} />
  );
}

/** Banner for users whose account is not linked to an identity (self-service pages need one). */
export function NoIdentity({ what }: { what?: string }) {
  const t = useT();
  return <Banner tone="info" title={t("p.noIdentity.title")} body={what ?? t("p.noIdentity.body")} />;
}
